<template>
    <div class="billing-manage" :class="{ 'billing-manage--light': !dark }">
        <div class="billing-manage__inner">
            <a
                v-if="currentTab !== 'contactDetails'"
                class="billing-manage__back"
                href="/billing/"
            >
                <v-icon size="16">$vuetify.icons.prev</v-icon>
                Back to Billing Accounts
            </a>

            <section
                v-if="currentTab !== 'contactDetails'"
                class="billing-manage__hero"
                data-billing-tour-overview
            >
                <div>
                    <div class="billing-manage__eyebrow">Billing Account</div>
                    <div class="billing-manage__title-row">
                        <h1>{{ account.company_name || 'Billing Account' }}</h1>
                        <billing-status-badge :status="accountStatus" />
                    </div>
                    <p>
                        Manage billing details, subscriptions and
                        administrators.
                    </p>
                </div>
                <div class="billing-manage__actions">
                    <v-btn
                        variant="outlined"
                        @click="currentTab = 'planAndBilling'"
                    >
                        <v-icon start>mdi-credit-card-outline</v-icon>
                        Update Payment Method
                    </v-btn>
                    <v-btn
                        color="primary"
                        @click="currentTab = 'contactDetails'"
                    >
                        <v-icon start>mdi-content-save-outline</v-icon>
                        Save Changes
                    </v-btn>
                </div>
            </section>

            <section
                v-if="currentTab !== 'contactDetails'"
                class="billing-manage__metrics"
            >
                <billing-metric-card
                    icon="mdi-file-document-outline"
                    label="Current Plan"
                    :value="planName"
                    :caption="planCaption"
                    tone="purple"
                />
                <billing-metric-card
                    icon="$vuetify.icons.team"
                    label="Administrators"
                    :value="adminCount"
                    caption="billing admins"
                    tone="orange"
                    data-billing-tour-seat-usage
                />
                <billing-metric-card
                    icon="mdi-credit-card-outline"
                    label="Payment Method"
                    :value="paymentLabel"
                    :caption="paymentCaption"
                    tone="green"
                />
                <billing-metric-card
                    icon="mdi-shield-check-outline"
                    label="Status"
                    :value="statusLabel"
                    :caption="statusCaption"
                    tone="cyan"
                />
            </section>

            <section class="billing-manage__body">
                <aside class="billing-manage__sidebar">
                    <div
                        v-if="currentTab === 'contactDetails'"
                        class="billing-manage__account-summary"
                    >
                        <a class="billing-manage__back" href="/billing/">
                            <v-icon size="16">$vuetify.icons.prev</v-icon>
                            Back to Billing Accounts
                        </a>
                        <div class="billing-manage__eyebrow">
                            Billing Account
                        </div>
                        <div class="billing-manage__account-title">
                            <strong>{{
                                account.company_name || 'Billing Account'
                            }}</strong>
                            <billing-status-badge :status="accountStatus" />
                        </div>
                    </div>
                    <billing-panel class="billing-manage__nav">
                        <button
                            v-for="item in navItems"
                            :key="item.value"
                            class="billing-manage__nav-item"
                            :class="{
                                'billing-manage__nav-item--active':
                                    currentTab === item.value,
                            }"
                            type="button"
                            @click="currentTab = item.value"
                        >
                            <v-icon size="24">{{ item.icon }}</v-icon>
                            <span>{{ item.label }}</span>
                        </button>
                    </billing-panel>
                    <billing-support-card class="billing-manage__support" />
                </aside>

                <billing-panel class="billing-manage__content">
                    <template v-if="currentTab === 'overview'">
                        <header class="billing-manage__section-header">
                            <h2>Overview</h2>
                            <p>A quick summary of your billing account.</p>
                        </header>

                        <div class="billing-manage__overview-grid">
                            <billing-panel class="billing-manage__summary-card">
                                <div class="billing-manage__summary-title">
                                    Billing Contact
                                </div>
                                <div class="billing-manage__summary-row">
                                    <span class="billing-manage__avatar">
                                        {{ contactInitials }}
                                    </span>
                                    <div>
                                        <strong>{{
                                            account.contact_name || 'No contact'
                                        }}</strong>
                                        <span>{{
                                            account.email || 'No email'
                                        }}</span>
                                    </div>
                                </div>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    @click="currentTab = 'contactDetails'"
                                >
                                    Edit Contact
                                    <v-icon end size="15">
                                        $vuetify.icons.edit
                                    </v-icon>
                                </v-btn>
                            </billing-panel>

                            <billing-panel class="billing-manage__summary-card">
                                <div class="billing-manage__summary-row">
                                    <billing-icon-tile
                                        icon="mdi-office-building-outline"
                                        tone="blue"
                                    />
                                    <div>
                                        <div
                                            class="billing-manage__summary-title"
                                        >
                                            Company
                                        </div>
                                        <strong>{{
                                            account.company_name || 'No company'
                                        }}</strong>
                                        <span>
                                            {{ companyDescription }}
                                        </span>
                                        <span>ABN {{ displayAbn }}</span>
                                    </div>
                                </div>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    @click="currentTab = 'contactDetails'"
                                >
                                    Edit Company
                                    <v-icon end size="15">
                                        $vuetify.icons.edit
                                    </v-icon>
                                </v-btn>
                            </billing-panel>

                            <billing-panel class="billing-manage__summary-card">
                                <div class="billing-manage__summary-row">
                                    <billing-icon-tile
                                        icon="mdi-clipboard-text-outline"
                                        tone="green"
                                    />
                                    <div>
                                        <div
                                            class="billing-manage__summary-title"
                                        >
                                            Subscription
                                        </div>
                                        <strong>{{ planName }}</strong>
                                        <span>{{ planCost }}</span>
                                    </div>
                                </div>
                                <dl class="billing-manage__facts">
                                    <div>
                                        <dt>Billing frequency</dt>
                                        <dd>{{ billingFrequency }}</dd>
                                    </div>
                                    <div>
                                        <dt>Billing mode</dt>
                                        <dd>{{ billingMode }}</dd>
                                    </div>
                                    <div>
                                        <dt>Status</dt>
                                        <dd>{{ statusLabel }}</dd>
                                    </div>
                                </dl>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    @click="currentTab = 'planAndBilling'"
                                >
                                    View Plan Details
                                    <v-icon end size="15"
                                        >mdi-open-in-new</v-icon
                                    >
                                </v-btn>
                            </billing-panel>

                            <billing-panel class="billing-manage__summary-card">
                                <div class="billing-manage__summary-row">
                                    <billing-icon-tile
                                        icon="mdi-credit-card-outline"
                                        tone="purple"
                                    />
                                    <div>
                                        <div
                                            class="billing-manage__summary-title"
                                        >
                                            Payment Method
                                        </div>
                                        <strong>{{ paymentLabel }}</strong>
                                        <span>{{ paymentCaption }}</span>
                                    </div>
                                </div>
                                <dl class="billing-manage__facts">
                                    <div>
                                        <dt>Cardholder</dt>
                                        <dd>{{ cardholder }}</dd>
                                    </div>
                                </dl>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    @click="currentTab = 'planAndBilling'"
                                >
                                    Update Payment Method
                                    <v-icon end size="15"
                                        >mdi-open-in-new</v-icon
                                    >
                                </v-btn>
                            </billing-panel>
                        </div>
                    </template>

                    <template v-else-if="currentTab === 'contactDetails'">
                        <div class="billing-manage__contact-header">
                            <div>
                                <h2>Contact Details</h2>
                                <p>
                                    Manage billing contacts, company information
                                    and legal details.
                                </p>
                            </div>
                            <div class="billing-manage__contact-actions">
                                <v-btn
                                    variant="outlined"
                                    :disabled="!hasUnsavedContactChanges"
                                    @click="cancelContactChanges"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    :disabled="
                                        !hasUnsavedContactChanges ||
                                        !contactValid ||
                                        !validAbn
                                    "
                                    :loading="savingContact"
                                    @click="saveContactDetails"
                                >
                                    <v-icon start
                                        >mdi-content-save-outline</v-icon
                                    >
                                    Save Changes
                                </v-btn>
                            </div>
                        </div>

                        <div class="billing-manage__contact-summary-grid">
                            <billing-panel
                                class="billing-manage__contact-summary-card"
                            >
                                <div class="billing-manage__summary-row">
                                    <span class="billing-manage__avatar">
                                        {{ contactFormInitials }}
                                    </span>
                                    <div>
                                        <div
                                            class="billing-manage__summary-title"
                                        >
                                            Billing Contact
                                        </div>
                                        <strong>{{
                                            contactForm.contact_name ||
                                            'No contact'
                                        }}</strong>
                                        <span>{{
                                            contactForm.email || 'No email'
                                        }}</span>
                                    </div>
                                </div>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    @click="focusContactName"
                                >
                                    Edit Contact
                                    <v-icon end size="15">
                                        $vuetify.icons.edit
                                    </v-icon>
                                </v-btn>
                            </billing-panel>

                            <billing-panel
                                class="billing-manage__contact-summary-card"
                            >
                                <div class="billing-manage__summary-row">
                                    <billing-icon-tile
                                        icon="mdi-office-building-outline"
                                        tone="blue"
                                    />
                                    <div>
                                        <div
                                            class="billing-manage__summary-title"
                                        >
                                            Company
                                        </div>
                                        <strong>{{
                                            contactForm.company_name ||
                                            'No company'
                                        }}</strong>
                                        <span>{{ contactCompanySummary }}</span>
                                        <span>ABN {{ contactDisplayAbn }}</span>
                                    </div>
                                </div>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    @click="focusCompanyName"
                                >
                                    Edit Company
                                    <v-icon end size="15">
                                        $vuetify.icons.edit
                                    </v-icon>
                                </v-btn>
                            </billing-panel>
                        </div>

                        <v-form v-model="contactValid">
                            <billing-panel class="billing-manage__form-card">
                                <div class="billing-manage__form-heading">
                                    <billing-icon-tile
                                        icon="$vuetify.icons.team"
                                        tone="purple"
                                    />
                                    <div>
                                        <h3>Contact Information</h3>
                                        <p>
                                            Update the primary billing contact
                                            details for this account.
                                        </p>
                                    </div>
                                </div>
                                <div class="billing-manage__form-grid">
                                    <v-text-field
                                        ref="contactNameField"
                                        v-model="contactForm.contact_name"
                                        label="Billing Contact Name"
                                        :rules="[required]"
                                        variant="outlined"
                                        density="comfortable"
                                    />
                                    <v-text-field
                                        v-model="contactForm.email"
                                        label="Billing Email"
                                        :rules="[required, emailRule]"
                                        variant="outlined"
                                        density="comfortable"
                                    />
                                </div>
                            </billing-panel>

                            <billing-panel class="billing-manage__form-card">
                                <div class="billing-manage__form-heading">
                                    <billing-icon-tile
                                        icon="mdi-clipboard-text-outline"
                                        tone="green"
                                    />
                                    <div>
                                        <h3>Company Information</h3>
                                        <p>Update your company details.</p>
                                    </div>
                                </div>
                                <div class="billing-manage__form-grid">
                                    <v-text-field
                                        ref="companyNameField"
                                        v-model="contactForm.company_name"
                                        label="Company Name"
                                        :rules="[required]"
                                        variant="outlined"
                                        density="comfortable"
                                    />
                                    <v-select
                                        v-model="contactForm.company_industry"
                                        label="Industry"
                                        :items="companyIndustryOptions"
                                        item-title="text"
                                        item-value="value"
                                        :rules="[required]"
                                        variant="outlined"
                                        density="comfortable"
                                    />
                                    <v-select
                                        v-model="contactForm.company_size"
                                        label="Company Size"
                                        :items="companySizeOptions"
                                        item-title="text"
                                        item-value="value"
                                        :rules="[required]"
                                        variant="outlined"
                                        density="comfortable"
                                    />
                                </div>
                            </billing-panel>

                            <billing-panel class="billing-manage__form-card">
                                <div class="billing-manage__form-heading">
                                    <billing-icon-tile
                                        icon="mdi-file-document-outline"
                                        tone="orange"
                                    />
                                    <div>
                                        <h3>Address & Legal</h3>
                                        <p>
                                            Provide your company address and
                                            legal information.
                                        </p>
                                    </div>
                                </div>
                                <v-alert
                                    v-if="!validAbn"
                                    type="warning"
                                    density="compact"
                                    variant="tonal"
                                    class="billing-manage__abn-alert"
                                >
                                    Invalid ABN.
                                </v-alert>
                                <div class="billing-manage__form-grid">
                                    <v-textarea
                                        v-model="contactForm.company_address"
                                        label="Company Address"
                                        :rules="[required]"
                                        variant="outlined"
                                        density="comfortable"
                                        rows="1"
                                        auto-grow
                                    />
                                    <v-text-field
                                        v-model="contactForm.abn"
                                        label="ABN"
                                        variant="outlined"
                                        density="comfortable"
                                    />
                                </div>
                            </billing-panel>
                        </v-form>

                        <billing-panel class="billing-manage__form-card">
                            <div class="billing-manage__form-heading">
                                <billing-icon-tile
                                    icon="mdi-information-outline"
                                    tone="blue"
                                />
                                <div>
                                    <h3>Account Metadata</h3>
                                    <p>
                                        System information for this billing
                                        account.
                                    </p>
                                </div>
                            </div>
                            <dl class="billing-manage__metadata-grid">
                                <div>
                                    <dt>Billing Account ID</dt>
                                    <dd>
                                        {{ account.uuid || billingAccountId }}
                                    </dd>
                                </div>
                                <div>
                                    <dt>Created</dt>
                                    <dd>Not available</dd>
                                </div>
                                <div>
                                    <dt>Last Updated</dt>
                                    <dd>Not available</dd>
                                </div>
                                <div>
                                    <dt>Created By</dt>
                                    <dd>
                                        {{
                                            account.created_by ||
                                            'Not available'
                                        }}
                                    </dd>
                                </div>
                            </dl>
                        </billing-panel>

                        <billing-panel
                            v-if="hasUnsavedContactChanges"
                            class="billing-manage__save-bar"
                        >
                            <span class="billing-manage__save-icon">
                                <v-icon size="18"
                                    >$vuetify.icons.complete</v-icon
                                >
                            </span>
                            <div>
                                <strong>You have unsaved changes</strong>
                                <p>
                                    Cancel or save your changes to update your
                                    billing account details.
                                </p>
                            </div>
                            <v-spacer />
                            <v-btn
                                variant="outlined"
                                @click="cancelContactChanges"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                color="primary"
                                :disabled="!contactValid || !validAbn"
                                :loading="savingContact"
                                @click="saveContactDetails"
                            >
                                <v-icon start>mdi-content-save-outline</v-icon>
                                Save Changes
                            </v-btn>
                        </billing-panel>
                    </template>

                    <template v-else-if="currentTab === 'planAndBilling'">
                        <header class="billing-manage__section-header">
                            <h2>Plan & Billing</h2>
                            <p>
                                Manage your subscription, payment method and
                                billing preferences.
                            </p>
                        </header>

                        <div class="billing-manage__plan-grid">
                            <billing-panel class="billing-manage__plan-card" data-billing-tour-current-plan>
                                <div class="billing-manage__form-heading">
                                    <billing-icon-tile
                                        icon="mdi-file-document-outline"
                                        tone="purple"
                                    />
                                    <div>
                                        <h3>Current Subscription</h3>
                                    </div>
                                </div>
                                <div class="billing-manage__subscription-title">
                                    <strong>{{ planName }}</strong>
                                    <billing-status-badge
                                        :status="accountStatus"
                                    />
                                </div>
                                <div class="billing-manage__subscription-price">
                                    {{ planCost }}
                                </div>
                                <dl class="billing-manage__plan-facts" data-billing-tour-dataset-usage>
                                    <div>
                                        <dt>
                                            <v-icon size="16"
                                                >mdi-calendar-month-outline</v-icon
                                            >
                                            Billing frequency
                                        </dt>
                                        <dd>{{ billingFrequency }}</dd>
                                    </div>
                                    <div data-billing-tour-storage-usage>
                                        <dt>
                                            <v-icon size="16"
                                                >mdi-harddisk-outline</v-icon
                                            >
                                            Storage usage
                                        </dt>
                                        <dd>{{ storageUsageLabel }}</dd>
                                    </div>
                                    <div>
                                        <dt>
                                            <v-icon size="16"
                                                >mdi-calendar-clock</v-icon
                                            >
                                            Next billing date
                                        </dt>
                                        <dd>{{ nextBillingDateLabel }}</dd>
                                    </div>
                                    <div>
                                        <dt>
                                            <v-icon size="16"
                                                >mdi-account-outline</v-icon
                                            >
                                            Last payment
                                        </dt>
                                        <dd>{{ lastPaymentLabel }}</dd>
                                    </div>
                                    <div>
                                        <dt>
                                            <v-icon size="16"
                                                >mdi-credit-card-outline</v-icon
                                            >
                                            Payment method
                                        </dt>
                                        <dd>{{ paymentLabel }}</dd>
                                    </div>
                                </dl>
                                <v-btn variant="outlined" size="small">
                                    View Plan Details
                                    <v-icon end size="15"
                                        >mdi-open-in-new</v-icon
                                    >
                                </v-btn>
                            </billing-panel>

                            <billing-panel class="billing-manage__plan-card">
                                <div class="billing-manage__form-heading">
                                    <billing-icon-tile
                                        icon="mdi-credit-card-outline"
                                        tone="green"
                                    />
                                    <div>
                                        <h3>Payment Method</h3>
                                    </div>
                                </div>
                                <div
                                    class="billing-manage__credit-card-preview"
                                >
                                    <span class="billing-manage__card-brand">
                                        {{ paymentBrand }}
                                    </span>
                                    <strong>{{ cardNumberPreview }}</strong>
                                    <span>{{ cardholder }}</span>
                                    <small>Expires</small>
                                    <span>{{ cardExpiryLabel }}</span>
                                    <em>{{ paymentBrand }}</em>
                                </div>
                                <div class="billing-manage__primary-payment">
                                    <v-icon size="17"
                                        >$vuetify.icons.complete</v-icon
                                    >
                                    <div>
                                        <strong>Primary payment method</strong>
                                        <p>
                                            This card will be charged for all
                                            future payments.
                                        </p>
                                    </div>
                                </div>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    @click="focusPaymentUpdate"
                                >
                                    Replace Card
                                </v-btn>
                            </billing-panel>
                        </div>

                        <billing-panel
                            ref="paymentUpdateCard"
                            class="billing-manage__payment-update-card"
                        >
                            <div class="billing-manage__form-heading">
                                <billing-icon-tile
                                    icon="mdi-clipboard-text-outline"
                                    tone="green"
                                />
                                <div>
                                    <h3>Update Payment Method</h3>
                                    <p>
                                        Update your card details. Your current
                                        subscription will continue
                                        uninterrupted.
                                    </p>
                                </div>
                            </div>
                            <stripe-form
                                :billing-info="billingInfo || {}"
                                :billing-plans="billingPlans"
                                :is-update-form="true"
                                class="billing-manage__stripe-form"
                            />
                        </billing-panel>

                        <div class="billing-manage__plan-grid">
                            <billing-panel class="billing-manage__mini-card" data-billing-tour-history>
                                <div class="billing-manage__form-heading">
                                    <billing-icon-tile
                                        icon="mdi-receipt-text-outline"
                                        tone="purple"
                                    />
                                    <div>
                                        <h3>Billing History</h3>
                                        <p>
                                            View your invoices and payment
                                            history.
                                        </p>
                                    </div>
                                </div>
                                <v-btn variant="outlined" size="small">
                                    View Billing History
                                    <v-icon end size="15"
                                        >mdi-open-in-new</v-icon
                                    >
                                </v-btn>
                            </billing-panel>

                            <billing-panel class="billing-manage__mini-card" data-billing-tour-upgrade>
                                <div class="billing-manage__form-heading">
                                    <billing-icon-tile
                                        icon="mdi-file-cancel-outline"
                                        tone="purple"
                                    />
                                    <div>
                                        <h3>Need to cancel?</h3>
                                        <p>
                                            If you need to cancel your
                                            subscription, please contact our
                                            support team.
                                        </p>
                                    </div>
                                </div>
                                <v-btn
                                    variant="outlined"
                                    size="small"
                                    href="/support/"
                                >
                                    Contact Support
                                    <v-icon end size="15"
                                        >mdi-open-in-new</v-icon
                                    >
                                </v-btn>
                            </billing-panel>
                        </div>

                        <billing-panel class="billing-manage__compliance-card">
                            <div class="billing-manage__compliance-copy">
                                <billing-icon-tile
                                    icon="mdi-lock-outline"
                                    tone="blue"
                                />
                                <div>
                                    <strong>Secure & Compliant</strong>
                                    <p>
                                        All payments are processed securely. We
                                        comply with PCI DSS standards.
                                    </p>
                                </div>
                            </div>
                            <div class="billing-manage__compliance-badges">
                                <span>
                                    <v-icon size="22"
                                        >mdi-shield-check-outline</v-icon
                                    >
                                    PCI DSS Compliant
                                </span>
                                <span>
                                    <v-icon size="22"
                                        >mdi-shield-lock-outline</v-icon
                                    >
                                    SSL Secured
                                </span>
                            </div>
                        </billing-panel>
                    </template>

                    <template v-else>
                        <div class="billing-manage__admin-header">
                            <div>
                                <h2>Administrators</h2>
                                <p>
                                    Manage users who have access to this billing
                                    account.
                                </p>
                            </div>
                            <v-btn color="primary" @click="openAdminDialog">
                                <v-icon start>$vuetify.icons.team</v-icon>
                                Add Administrator
                            </v-btn>
                        </div>

                        <div class="billing-manage__admin-toolbar">
                            <v-text-field
                                v-model="adminSearch"
                                density="compact"
                                hide-details
                                prepend-inner-icon="mdi-magnify"
                                placeholder="Search administrators..."
                                variant="outlined"
                            />
                            <v-select
                                v-model="adminRoleFilter"
                                :items="adminRoleOptions"
                                density="compact"
                                hide-details
                                item-title="text"
                                item-value="value"
                                variant="outlined"
                            />
                        </div>

                        <billing-panel class="billing-manage__admin-table">
                            <div class="billing-manage__admin-table-head">
                                <span>Administrator</span>
                                <span>Email</span>
                                <span>Role</span>
                                <span>Added</span>
                                <span>Actions</span>
                            </div>
                            <div
                                v-for="admin in filteredAdmins"
                                :key="admin.id || admin.email"
                                class="billing-manage__admin-row"
                            >
                                <div class="billing-manage__admin-person">
                                    <span class="billing-manage__admin-avatar">
                                        {{ adminInitials(admin) }}
                                    </span>
                                    <strong>{{ admin.name }}</strong>
                                    <span
                                        v-if="admin.isCurrentUser"
                                        class="billing-manage__self-badge"
                                    >
                                        You
                                    </span>
                                </div>
                                <span class="billing-manage__admin-email">
                                    {{ admin.email }}
                                </span>
                                <span>
                                    <span
                                        class="billing-manage__role-badge"
                                        :class="
                                            adminRole(admin) === 'Owner'
                                                ? 'billing-manage__role-badge--owner'
                                                : 'billing-manage__role-badge--admin'
                                        "
                                    >
                                        {{ adminRole(admin) }}
                                    </span>
                                </span>
                                <span>{{ adminAddedLabel(admin) }}</span>
                                <span class="billing-manage__admin-actions">
                                    <v-btn
                                        icon
                                        variant="text"
                                        size="small"
                                        :disabled="admin.isCurrentUser"
                                        @click="removeAdministrator(admin)"
                                    >
                                        <v-icon
                                            >$vuetify.icons.moreOptions</v-icon
                                        >
                                    </v-btn>
                                </span>
                            </div>
                            <div
                                v-if="!filteredAdmins.length"
                                class="billing-manage__admin-empty-row"
                            >
                                No administrators match your filters.
                            </div>
                        </billing-panel>

                        <billing-panel class="billing-manage__invite-card">
                            <span class="billing-manage__invite-icon">
                                <v-icon size="34">$vuetify.icons.team</v-icon>
                            </span>
                            <h3>Add more administrators</h3>
                            <p>
                                Invite team members to help manage billing and
                                subscriptions.
                            </p>
                            <v-btn color="primary" @click="openAdminDialog">
                                <v-icon start>$vuetify.icons.plus</v-icon>
                                Add Administrator
                            </v-btn>
                        </billing-panel>

                        <billing-panel class="billing-manage__roles-card">
                            <h3>About administrator roles</h3>
                            <div class="billing-manage__roles-grid">
                                <div>
                                    <billing-icon-tile
                                        icon="$vuetify.icons.team"
                                        tone="purple"
                                    />
                                    <div>
                                        <strong>Owner</strong>
                                        <p>
                                            Full access to billing settings,
                                            payment methods, plans and
                                            administrators.
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <billing-icon-tile
                                        icon="$vuetify.icons.team"
                                        tone="blue"
                                    />
                                    <div>
                                        <strong>Administrator</strong>
                                        <p>
                                            Can view billing information, manage
                                            payment methods and update
                                            subscription settings.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </billing-panel>

                        <billing-panel class="billing-manage__compliance-card">
                            <div class="billing-manage__compliance-copy">
                                <billing-icon-tile
                                    icon="mdi-lock-outline"
                                    tone="blue"
                                />
                                <div>
                                    <strong>Your data is secure</strong>
                                    <p>
                                        All account access is protected and
                                        audited.
                                    </p>
                                </div>
                            </div>
                            <div class="billing-manage__compliance-badges">
                                <span>
                                    <v-icon size="22"
                                        >mdi-shield-check-outline</v-icon
                                    >
                                    PCI DSS Compliant
                                </span>
                                <span>
                                    <v-icon size="22"
                                        >mdi-shield-lock-outline</v-icon
                                    >
                                    SSL Secured
                                </span>
                            </div>
                        </billing-panel>
                    </template>
                </billing-panel>
            </section>
        </div>
        <v-dialog v-model="adminDialog" max-width="560px">
            <v-card class="billing-manage__admin-dialog">
                <div class="billing-manage__dialog-title">
                    <div>
                        <div class="billing-manage__eyebrow">
                            Billing Account
                        </div>
                        <h2>Add Administrator</h2>
                    </div>
                    <v-btn icon variant="text" @click="adminDialog = false">
                        <v-icon>$vuetify.icons.close</v-icon>
                    </v-btn>
                </div>
                <v-autocomplete
                    v-model="adminEmail"
                    v-model:search="adminEmailSearch"
                    :items="adminEmailResults"
                    :loading="loadingAdminEmails"
                    :menu-props="{ maxHeight: 280 }"
                    clearable
                    autocomplete="off"
                    hide-details="auto"
                    item-title="title"
                    item-value="value"
                    label="Search team members by email"
                    placeholder="Type an email address"
                    density="comfortable"
                    class="billing-manage__admin-search"
                    variant="outlined"
                >
                    <template #no-data>
                        <v-list-item>
                            <v-list-item-title>
                                {{
                                    adminEmailSearch &&
                                    adminEmailSearch.length > 3
                                        ? 'No matching email addresses.'
                                        : 'Start typing to search for an email address.'
                                }}
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                </v-autocomplete>
                <div class="billing-manage__dialog-actions">
                    <v-btn variant="outlined" @click="adminDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        :disabled="!adminEmail"
                        :loading="addingAdministrator"
                        @click="addAdministrator"
                    >
                        <v-icon start>$vuetify.icons.plus</v-icon>
                        Add Administrator
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
        <generic-snack-bar ref="snackbar" />
    </div>
</template>

<script>
import axios from 'axios';
import BillingIconTile from 'components/BillingAccounts/Shared/BillingIconTile.vue';
import BillingMetricCard from 'components/BillingAccounts/Shared/BillingMetricCard.vue';
import BillingPanel from 'components/BillingAccounts/Shared/BillingPanel.vue';
import BillingStatusBadge from 'components/BillingAccounts/Shared/BillingStatusBadge.vue';
import BillingSupportCard from 'components/BillingAccounts/Shared/BillingSupportCard.vue';
import GenericSnackBar from 'components/GenericSnackbar/GenericSnackBar.vue';
import StripeForm from 'components/Stripe/StripeForm/StripeForm.vue';
import {
    buildBillingSubscriptionTourSteps,
    startBillingSubscriptionTour,
} from 'js/onboarding/billingSubscriptionTour';

export default {
    name: 'ManageBillingAccount',
    components: {
        BillingIconTile,
        BillingMetricCard,
        BillingPanel,
        BillingStatusBadge,
        BillingSupportCard,
        GenericSnackBar,
        StripeForm,
    },
    props: {
        billingAccountId: {
            type: String,
            default: null,
        },
        billingPlans: {
            type: Array,
            default: function () {
                return [];
            },
        },
        dark: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            currentTab: 'overview',
            account: {
                company_name: '',
                contact_name: '',
                email: '',
                company_address: '',
                company_industry_display: '',
                company_size_display: '',
                abn: '',
                uuid: '',
                created_by: '',
            },
            contactForm: {
                contact_name: '',
                email: '',
                company_name: '',
                company_address: '',
                company_industry: '',
                company_size: '',
                abn: '',
            },
            originalContactForm: null,
            contactValid: false,
            validAbn: true,
            savingContact: false,
            billingInfo: null,
            admins: [],
            adminSearch: '',
            adminRoleFilter: 'all',
            adminDialog: false,
            adminEmail: '',
            adminEmailSearch: '',
            adminEmailResults: [],
            loadingAdminEmails: false,
            addingAdministrator: false,
            billingTourStartTimerId: null,
            adminRoleOptions: [
                { text: 'All Roles', value: 'all' },
                { text: 'Owner', value: 'Owner' },
                { text: 'Administrator', value: 'Administrator' },
            ],
            required: (value) => !!value || 'Required field.',
            emailRule: (value) => {
                const pattern =
                    /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\d-A-Za-z]+\.)+[A-Za-z]{2,}))$/;
                return pattern.test(value) || 'Must be a valid e-mail address.';
            },
            companyIndustryOptions: [
                { text: 'Agriculture and Farming', value: 'ag/farm' },
                { text: 'Asset Management and Maintenance', value: 'asset' },
                { text: 'Building and Construction', value: 'building' },
                { text: 'Councils and Government', value: 'councils/gov' },
                { text: 'Drones & Aerial Services', value: 'service' },
                { text: 'Emergency Services', value: 'emergency' },
                { text: 'Energy (Renewable)', value: 'energy-renew' },
                { text: 'Energy (Oil and Gas)', value: 'energy-oil/gas' },
                { text: 'Energy (Other Types)', value: 'energy-other' },
                { text: 'Infrastructure', value: 'infra' },
                { text: 'Insurance and Compliance', value: 'insurance' },
                { text: 'Mining and Resources', value: 'mine/res' },
                { text: 'Real Estate', value: 'realestate' },
                { text: 'Telecommunications', value: 'telco' },
                { text: 'All Other Industries', value: 'other' },
            ],
            companySizeOptions: [
                { value: '1', text: 'Just Me' },
                { value: 'small', text: '2-10' },
                { value: 'medium', text: '10-50' },
                { value: 'large', text: '50-200' },
                { value: 'corporate', text: '200+' },
            ],
            navItems: [
                {
                    value: 'overview',
                    label: 'Overview',
                    icon: 'mdi-home-outline',
                },
                {
                    value: 'contactDetails',
                    label: 'Contact Details',
                    icon: '$vuetify.icons.profile',
                },
                {
                    value: 'planAndBilling',
                    label: 'Plan & Billing',
                    icon: 'mdi-credit-card-outline',
                },
                {
                    value: 'accountAdministrators',
                    label: 'Administrators',
                    icon: '$vuetify.icons.team',
                },
            ],
        };
    },
    computed: {
        accountStatus() {
            if (this.billingInfo && this.billingInfo.hasStripeSubscription) {
                return 'active';
            }
            if (this.billingInfo && this.billingInfo.hasAssignedPlan) {
                return 'active';
            }
            return 'inactive';
        },
        statusLabel() {
            return this.accountStatus === 'active' ? 'Active' : 'Inactive';
        },
        statusCaption() {
            return this.accountStatus === 'active'
                ? 'Billing profile enabled'
                : 'No active subscription';
        },
        plan() {
            return this.billingInfo && this.billingInfo.plan
                ? this.billingInfo.plan
                : null;
        },
        planName() {
            return this.plan && this.plan.name ? this.plan.name : 'No plan';
        },
        planCaption() {
            return this.planCost === 'Plan cost unavailable'
                ? 'subscription'
                : this.planCost;
        },
        storageUsageLabel() {
            const used =
                (this.billingInfo && this.billingInfo.storageUsedBytes) ||
                this.account.storage_used_bytes ||
                0;
            const allowed =
                (this.billingInfo && this.billingInfo.storageAllowedBytes) ||
                0;
            if (!used && !allowed) {
                return 'Not available';
            }
            const usedGb = Math.max(Number(used) || 0, 0) / 1024 ** 3;
            const allowedGb = Math.max(Number(allowed) || 0, 0) / 1024 ** 3;
            return `${usedGb.toFixed(1)} GB / ${allowedGb ? `${allowedGb.toFixed(1)} GB` : 'Custom'}`;
        },
        planCost() {
            if (!this.plan || !this.plan.cost) {
                return 'Plan cost unavailable';
            }
            const currency = this.plan.currency || 'USD';
            const symbol = currency === 'EUR' ? 'EUR ' : '$';
            return `${symbol}${this.plan.cost} ${currency} / month`;
        },
        billingFrequency() {
            if (!this.plan || !this.plan.frequency) {
                return 'Not set';
            }
            return Number(this.plan.frequency) === 1
                ? 'Every 1 month'
                : `Every ${this.plan.frequency} months`;
        },
        billingMode() {
            const mode = this.billingInfo && this.billingInfo.billingMode;
            if (mode === 'stripe_subscription') {
                return 'Stripe subscription';
            }
            if (mode === 'manual_invoice') {
                return 'Manual invoice';
            }
            return 'Not configured';
        },
        card() {
            return this.billingInfo && this.billingInfo.card
                ? this.billingInfo.card
                : null;
        },
        paymentLabel() {
            if (!this.card) {
                return 'No card';
            }
            const brand = this.card.brand || 'Card';
            return `${brand} **** ${this.card.last4}`;
        },
        paymentCaption() {
            if (!this.card) {
                return 'Payment method needed';
            }
            return `Expires ${this.card.expiry || 'unknown'}`;
        },
        cardholder() {
            return (
                (this.card && this.card.contact_name) ||
                this.account.contact_name ||
                'Not provided'
            );
        },
        paymentBrand() {
            return this.card && this.card.brand
                ? this.card.brand.toUpperCase()
                : 'CARD';
        },
        cardNumberPreview() {
            return this.card ? `**** **** **** ${this.card.last4}` : 'No card';
        },
        cardExpiryLabel() {
            return this.card && this.card.expiry
                ? this.card.expiry
                : 'Not available';
        },
        nextBillingDateLabel() {
            return this.billingInfo && this.billingInfo.next_billing_date
                ? this.billingInfo.next_billing_date
                : 'Not available';
        },
        lastPaymentLabel() {
            return this.billingInfo && this.billingInfo.last_payment_date
                ? this.billingInfo.last_payment_date
                : 'Not available';
        },
        adminCount() {
            return this.admins.length;
        },
        filteredAdmins() {
            const query = this.adminSearch.trim().toLowerCase();
            return this.admins.filter((admin) => {
                const role = this.adminRole(admin);
                const haystack = [admin.name, admin.email, role]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                const matchesSearch = !query || haystack.includes(query);
                const matchesRole =
                    this.adminRoleFilter === 'all' ||
                    role === this.adminRoleFilter;
                return matchesSearch && matchesRole;
            });
        },
        contactInitials() {
            const name = this.account.contact_name || this.account.company_name;
            const words = String(name || '')
                .trim()
                .split(/\s+/)
                .filter(Boolean);
            if (!words.length) {
                return 'BA';
            }
            if (words.length === 1) {
                return words[0].slice(0, 2).toUpperCase();
            }
            return `${words[0][0]}${words[1][0]}`.toUpperCase();
        },
        companyDescription() {
            return [
                this.account.company_industry_display,
                this.account.company_size_display,
            ]
                .filter(Boolean)
                .join(' - ');
        },
        displayAbn() {
            return this.account.abn || 'None provided';
        },
        contactFormInitials() {
            const name =
                this.contactForm.contact_name || this.contactForm.company_name;
            const words = String(name || '')
                .trim()
                .split(/\s+/)
                .filter(Boolean);
            if (!words.length) {
                return 'BA';
            }
            if (words.length === 1) {
                return words[0].slice(0, 2).toUpperCase();
            }
            return `${words[0][0]}${words[1][0]}`.toUpperCase();
        },
        contactCompanySummary() {
            return [
                this.optionText(
                    this.companyIndustryOptions,
                    this.contactForm.company_industry
                ),
                this.optionText(
                    this.companySizeOptions,
                    this.contactForm.company_size
                ),
            ]
                .filter(Boolean)
                .join(' - ');
        },
        contactDisplayAbn() {
            return this.contactForm.abn || 'None provided';
        },
        hasUnsavedContactChanges() {
            if (!this.originalContactForm) {
                return false;
            }
            return (
                JSON.stringify(this.normalizedContactForm(this.contactForm)) !==
                JSON.stringify(
                    this.normalizedContactForm(this.originalContactForm)
                )
            );
        },
    },
    watch: {
        adminEmailSearch(value) {
            if (!value || value.length <= 3) {
                this.adminEmailResults = [];
                return;
            }
            this.getAdminEmailResults(value);
        },
        'contactForm.abn': function (value) {
            const normalizedValue = String(value || '').replace(/[ _]/g, '');

            if (normalizedValue.length === 0) {
                this.validAbn = true;
                return;
            }

            if (normalizedValue.length !== 11) {
                this.validAbn = false;
                return;
            }

            const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
            const checksum = normalizedValue
                .split('')
                .map(Number)
                .reduce((total, digit, index) => {
                    const weightedDigit = index === 0 ? digit - 1 : digit;
                    return total + weightedDigit * weights[index];
                }, 0);

            this.validAbn = Boolean(checksum && checksum % 89 === 0);
        },
    },
    mounted() {
        this.refreshOverview();
        this.currentTab = 'planAndBilling';
        this.queueBillingSubscriptionTourStart();
    },
    beforeUnmount() {
        if (this.billingTourStartTimerId) {
            window.clearTimeout(this.billingTourStartTimerId);
            this.billingTourStartTimerId = null;
        }
    },
    methods: {
        refreshOverview() {
            this.getBillingAccount();
            this.getPlanAndBillingInfo();
            this.getAdministrators();
        },
        queueBillingSubscriptionTourStart(delay = 180) {
            if (this.billingTourStartTimerId) {
                window.clearTimeout(this.billingTourStartTimerId);
            }

            this.billingTourStartTimerId = window.setTimeout(() => {
                this.startBillingSubscriptionTourWhenReady();
            }, delay);
        },
        async startBillingSubscriptionTourWhenReady() {
            this.billingTourStartTimerId = null;

            if (this.currentTab !== 'planAndBilling') {
                return;
            }

            await this.$nextTick();

            const billingTourTargetsReady = buildBillingSubscriptionTourSteps().every(
                (step) => !step.target || document.querySelector(step.target)
            );
            if (!billingTourTargetsReady) {
                this.queueBillingSubscriptionTourStart(120);
                return;
            }

            startBillingSubscriptionTour({
                context: {
                    user: window.USER || '',
                },
            });
        },
        getBillingAccount() {
            axios
                .get(
                    `/api/billing_account/${this.billingAccountId}/get_billing_account/`
                )
                .then((response) => response.data)
                .then((data) => {
                    Object.assign(this.account, data);
                    this.setContactForm(data);
                });
        },
        getPlanAndBillingInfo() {
            axios
                .get(
                    `/api/billing/${this.billingAccountId}/get_plan_and_billing_info/`
                )
                .then((response) => response.data)
                .then((data) => {
                    this.billingInfo = data;
                });
        },
        getAdministrators() {
            axios
                .get(`/api/billing_account/${this.billingAccountId}/get_admins`)
                .then((response) => response.data)
                .then((data) => {
                    this.admins = data.administrators || [];
                });
        },
        adminInitials(admin) {
            const name = admin.name || admin.email || '';
            const words = name.trim().split(/\s+/).filter(Boolean);
            if (!words.length) {
                return 'AD';
            }
            if (words.length === 1) {
                return words[0].slice(0, 2).toUpperCase();
            }
            return `${words[0][0]}${words[1][0]}`.toUpperCase();
        },
        adminRole(admin) {
            return admin.isOwner ? 'Owner' : 'Administrator';
        },
        adminAddedLabel() {
            return 'Not available';
        },
        openAdminDialog() {
            this.adminDialog = true;
            this.adminEmail = '';
            this.adminEmailSearch = '';
            this.adminEmailResults = [];
        },
        getAdminEmailResults(value) {
            this.loadingAdminEmails = true;
            axios
                .get(
                    `/api/billing_account/${this.billingAccountId}/get_emails?email=${value}`
                )
                .then((response) => response.data)
                .then((data) => {
                    const results = data.results || [];
                    this.adminEmailResults = results
                        .map((result) => ({
                            title: result.email,
                            value: result.email,
                        }))
                        .filter((result) =>
                            result.title
                                .toLowerCase()
                                .includes(value.toLowerCase())
                        );
                    this.loadingAdminEmails = false;
                })
                .catch(() => {
                    this.loadingAdminEmails = false;
                    this.$refs.snackbar.show(
                        'Could not search users due to a server error.'
                    );
                });
        },
        addAdministrator() {
            if (!this.adminEmail) {
                return;
            }
            this.addingAdministrator = true;
            axios
                .post(`/api/billing_account/${this.billingAccountId}/add/`, {
                    email: this.adminEmail,
                })
                .then((response) => response.data)
                .then((data) => {
                    this.addingAdministrator = false;
                    if (data.success) {
                        this.adminDialog = false;
                        this.getAdministrators();
                    }
                    this.$refs.snackbar.show(data.message);
                })
                .catch(() => {
                    this.addingAdministrator = false;
                    this.$refs.snackbar.show(
                        'This user could not be added due to a server error.'
                    );
                });
        },
        removeAdministrator(admin) {
            if (admin.isCurrentUser || !admin.removeAdminUrl) {
                return;
            }
            if (
                !window.confirm(
                    `Are you sure you want to remove ${admin.name} as an administrator?`
                )
            ) {
                return;
            }
            axios
                .get(admin.removeAdminUrl)
                .then((response) => response.data)
                .then((data) => {
                    this.$refs.snackbar.show(data.message);
                    this.getAdministrators();
                })
                .catch(() => {
                    this.$refs.snackbar.show(
                        'Could not remove this administrator due to a server error.'
                    );
                });
        },
        setContactForm(data) {
            this.contactForm = {
                contact_name: data.contact_name || '',
                email: data.email || '',
                company_name: data.company_name || '',
                company_address: data.company_address || '',
                company_industry: data.company_industry || '',
                company_size: data.company_size || '',
                abn: data.abn || '',
            };
            this.originalContactForm = { ...this.contactForm };
        },
        normalizedContactForm(form) {
            return {
                contact_name: form.contact_name || '',
                email: form.email || '',
                company_name: form.company_name || '',
                company_address: form.company_address || '',
                company_industry: form.company_industry || '',
                company_size: form.company_size || '',
                abn: form.abn || '',
            };
        },
        optionText(options, value) {
            const option = options.find((item) => item.value === value);
            return option ? option.text : '';
        },
        focusContactName() {
            this.$refs.contactNameField?.focus?.();
        },
        focusCompanyName() {
            this.$refs.companyNameField?.focus?.();
        },
        focusPaymentUpdate() {
            const panel = this.$refs.paymentUpdateCard?.$el;
            if (panel && typeof panel.scrollIntoView === 'function') {
                panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        cancelContactChanges() {
            if (this.originalContactForm) {
                this.contactForm = { ...this.originalContactForm };
            }
        },
        saveContactDetails() {
            if (!this.contactValid || !this.validAbn) {
                return;
            }
            this.savingContact = true;
            axios
                .post(
                    `/api/billing/${this.billingAccountId}/edit/`,
                    JSON.stringify({ billingAccount: this.contactForm })
                )
                .then((response) => response.data)
                .then((data) => {
                    this.savingContact = false;
                    if (data.success) {
                        Object.assign(this.account, {
                            ...this.contactForm,
                            company_industry_display: this.optionText(
                                this.companyIndustryOptions,
                                this.contactForm.company_industry
                            ),
                            company_size_display: this.optionText(
                                this.companySizeOptions,
                                this.contactForm.company_size
                            ),
                        });
                        this.originalContactForm = { ...this.contactForm };
                    }
                    this.$refs.snackbar.show(data.message);
                })
                .catch(() => {
                    this.savingContact = false;
                    this.$refs.snackbar.show(
                        "Your changes couldn't be saved. Please try again."
                    );
                });
        },
    },
};
</script>

<style scoped>
.billing-manage {
    --billing-bg:
        radial-gradient(
            circle at top right,
            rgba(42, 70, 171, 0.22),
            transparent 34rem
        ),
        #061321;
    --billing-card-bg: rgba(20, 36, 56, 0.78);
    --billing-border: rgba(153, 162, 185, 0.22);
    --billing-border-soft: rgba(153, 162, 185, 0.14);
    --billing-muted: #aab5c8;
    --billing-text: #f6f9ff;
    background: var(--billing-bg);
    color: var(--billing-text);
    min-height: calc(100vh - 96px);
    padding: 28px clamp(18px, 5vw, 80px) 56px;
}

.billing-manage--light {
    --billing-bg: #f7f9fe;
    --billing-card-bg: #ffffff;
    --billing-border: #e0e7f4;
    --billing-border-soft: #edf1f8;
    --billing-muted: #6f778a;
    --billing-text: #172033;
}

.billing-manage__inner {
    margin: 0 auto;
    max-width: 1380px;
}

.billing-manage__back {
    align-items: center;
    color: var(--billing-text);
    display: inline-flex;
    font-size: 14px;
    gap: 8px;
    margin-bottom: 28px;
    text-decoration: none;
}

.billing-manage__hero {
    align-items: flex-start;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    margin-bottom: 22px;
}

.billing-manage__eyebrow {
    color: #3c64f4;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.billing-manage__title-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.billing-manage h1,
.billing-manage h2 {
    color: var(--billing-text);
    font-weight: 800;
    line-height: 1.15;
    margin: 0;
}

.billing-manage h1 {
    font-size: 30px;
}

.billing-manage h2 {
    font-size: 22px;
}

.billing-manage__hero p,
.billing-manage__section-header p {
    color: var(--billing-muted);
    font-size: 15px;
    line-height: 1.55;
    margin: 8px 0 0;
}

.billing-manage__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-end;
}

.billing-manage__metrics {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-bottom: 22px;
}

.billing-manage__body {
    align-items: start;
    display: grid;
    gap: 16px;
    grid-template-columns: 240px minmax(0, 1fr);
}

.billing-manage__sidebar {
    display: grid;
    gap: 14px;
}

.billing-manage__account-summary {
    padding: 0 2px 8px;
}

.billing-manage__account-summary .billing-manage__back {
    margin-bottom: 26px;
}

.billing-manage__account-title {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.billing-manage__account-title strong {
    color: var(--billing-text);
    font-size: 18px;
    line-height: 1.35;
}

.billing-manage__nav {
    display: grid;
    gap: 6px;
    padding: 14px;
}

.billing-manage__nav-item {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: var(--billing-muted);
    cursor: pointer;
    display: flex;
    font: inherit;
    gap: 12px;
    min-height: 46px;
    padding: 0 14px;
    text-align: left;
}

.billing-manage__nav-item--active {
    background: rgba(60, 100, 244, 0.16);
    color: var(--billing-text);
    font-weight: 700;
    position: relative;
}

.billing-manage__nav-item--active::before {
    background: #3c64f4;
    border-radius: 999px;
    content: '';
    height: 100%;
    left: -14px;
    position: absolute;
    width: 3px;
}

.billing-manage__content {
    min-height: 520px;
    padding: 22px;
}

.billing-manage__contact-header {
    align-items: flex-start;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    margin-bottom: 22px;
}

.billing-manage__contact-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-end;
}

.billing-manage__section-header {
    margin-bottom: 18px;
}

.billing-manage__overview-grid,
.billing-manage__contact-summary-grid {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.billing-manage__summary-card,
.billing-manage__contact-summary-card {
    padding: 20px;
}

.billing-manage__summary-title {
    color: var(--billing-text);
    font-size: 14px;
    font-weight: 800;
    margin-bottom: 12px;
}

.billing-manage__summary-row {
    align-items: center;
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
}

.billing-manage__summary-row strong {
    color: var(--billing-text);
    display: block;
    font-size: 16px;
    line-height: 1.35;
}

.billing-manage__summary-row span {
    color: var(--billing-muted);
    display: block;
    font-size: 13px;
    margin-top: 4px;
}

.billing-manage__avatar {
    align-items: center;
    background: linear-gradient(135deg, #4d8dff, #6e4fc9);
    border-radius: 999px;
    color: #ffffff !important;
    display: inline-flex !important;
    flex: 0 0 auto;
    font-size: 18px !important;
    font-weight: 800;
    height: 48px;
    justify-content: center;
    width: 48px;
}

.billing-manage__facts {
    display: grid;
    gap: 8px;
    margin: 10px 0 18px;
}

.billing-manage__facts div {
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.billing-manage__facts dt {
    color: var(--billing-muted);
    font-size: 13px;
}

.billing-manage__facts dd {
    color: var(--billing-text);
    font-size: 13px;
    margin: 0;
}

.billing-manage__form-card {
    margin-top: 14px;
    padding: 20px;
}

.billing-manage__form-heading {
    align-items: flex-start;
    display: flex;
    gap: 16px;
    margin-bottom: 18px;
}

.billing-manage__form-heading h3 {
    color: var(--billing-text);
    font-size: 16px;
    font-weight: 800;
    line-height: 1.25;
    margin: 0 0 6px;
}

.billing-manage__form-heading p {
    color: var(--billing-muted);
    font-size: 13px;
    line-height: 1.45;
    margin: 0;
}

.billing-manage__form-grid {
    display: grid;
    gap: 16px 24px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.billing-manage__form-grid > :only-child {
    grid-column: 1 / -1;
}

.billing-manage__abn-alert {
    margin-bottom: 14px;
}

.billing-manage__metadata-grid {
    display: grid;
    gap: 18px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 0;
}

.billing-manage__metadata-grid dt {
    color: var(--billing-muted);
    font-size: 12px;
    margin-bottom: 8px;
}

.billing-manage__metadata-grid dd {
    color: var(--billing-text);
    font-size: 13px;
    margin: 0;
    overflow-wrap: anywhere;
}

.billing-manage__save-bar {
    align-items: center;
    bottom: 14px;
    display: flex;
    gap: 14px;
    margin-top: 14px;
    padding: 16px 18px;
    position: sticky;
    z-index: 2;
}

.billing-manage__save-bar strong {
    color: var(--billing-text);
    display: block;
    font-size: 14px;
    margin-bottom: 3px;
}

.billing-manage__save-bar p {
    color: var(--billing-muted);
    font-size: 13px;
    margin: 0;
}

.billing-manage__save-icon {
    align-items: center;
    background: rgba(75, 200, 167, 0.2);
    border-radius: 999px;
    color: #69d9b8;
    display: inline-flex;
    flex: 0 0 auto;
    height: 34px;
    justify-content: center;
    width: 34px;
}

.billing-manage__plan-grid {
    display: grid;
    gap: 18px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 18px;
}

.billing-manage__plan-card,
.billing-manage__mini-card,
.billing-manage__payment-update-card,
.billing-manage__compliance-card {
    padding: 20px;
}

.billing-manage__subscription-title {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 8px 0 4px 64px;
}

.billing-manage__subscription-title strong {
    color: var(--billing-text);
    font-size: 21px;
    font-weight: 800;
}

.billing-manage__subscription-price {
    border-bottom: 1px solid var(--billing-border-soft);
    color: var(--billing-text);
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 20px 64px;
    padding: 0 0 22px;
}

.billing-manage__plan-facts {
    display: grid;
    gap: 16px;
    margin: 0 0 28px;
}

.billing-manage__plan-facts div {
    align-items: center;
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 0.75fr);
}

.billing-manage__plan-facts dt {
    align-items: center;
    color: var(--billing-muted);
    display: inline-flex;
    font-size: 14px;
    gap: 10px;
}

.billing-manage__plan-facts dd {
    color: var(--billing-text);
    font-size: 14px;
    margin: 0;
}

.billing-manage__credit-card-preview {
    background:
        radial-gradient(
            circle at 82% 72%,
            rgba(255, 255, 255, 0.12),
            transparent 8rem
        ),
        rgba(18, 35, 55, 0.8);
    border: 1px solid var(--billing-border);
    border-radius: 8px;
    color: #ffffff;
    display: grid;
    gap: 12px;
    margin-bottom: 18px;
    min-height: 208px;
    padding: 24px;
    position: relative;
}

.billing-manage--light .billing-manage__credit-card-preview {
    background:
        radial-gradient(
            circle at 82% 72%,
            rgba(60, 100, 244, 0.12),
            transparent 8rem
        ),
        #172033;
}

.billing-manage__credit-card-preview strong {
    font-size: 23px;
    letter-spacing: 0.08em;
}

.billing-manage__credit-card-preview small {
    color: rgba(255, 255, 255, 0.62);
    font-size: 11px;
}

.billing-manage__credit-card-preview em {
    bottom: 24px;
    color: rgba(255, 255, 255, 0.14);
    font-size: 26px;
    font-style: italic;
    font-weight: 800;
    position: absolute;
    right: 24px;
}

.billing-manage__card-brand {
    align-items: center;
    background: #1e62dd;
    border-radius: 6px;
    display: inline-flex;
    font-size: 15px;
    font-weight: 800;
    justify-content: center;
    min-height: 32px;
    width: 60px;
}

.billing-manage__primary-payment {
    align-items: flex-start;
    color: #4bc8a7;
    display: flex;
    gap: 10px;
    margin-bottom: 18px;
}

.billing-manage__primary-payment strong {
    color: var(--billing-text);
    display: block;
    font-size: 13px;
    margin-bottom: 4px;
}

.billing-manage__primary-payment p {
    color: var(--billing-muted);
    font-size: 13px;
    margin: 0;
}

.billing-manage__payment-update-card {
    margin-top: 18px;
}

.billing-manage__stripe-form {
    max-width: none;
    padding: 0;
}

.billing-manage__stripe-form :deep(.v-container) {
    max-width: none !important;
    padding: 0;
}

.billing-manage__stripe-form :deep(.v-row) {
    margin: 0;
}

.billing-manage__stripe-form :deep(#card-element) {
    background: rgba(7, 18, 33, 0.34);
    border: 1px solid var(--billing-border);
    border-radius: 6px;
    box-shadow: none;
    color: var(--billing-text);
    min-height: 44px;
    width: 100%;
}

.billing-manage--light .billing-manage__stripe-form :deep(#card-element) {
    background: #ffffff;
}

.billing-manage__stripe-form :deep(#upgrade-plan) {
    width: 100%;
}

.billing-manage__mini-card {
    min-height: 170px;
}

.billing-manage__compliance-card {
    align-items: center;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    margin-top: 18px;
}

.billing-manage__compliance-copy {
    align-items: center;
    display: flex;
    gap: 16px;
}

.billing-manage__compliance-copy strong {
    color: var(--billing-text);
    display: block;
    font-size: 14px;
    margin-bottom: 4px;
}

.billing-manage__compliance-copy p {
    color: var(--billing-muted);
    font-size: 13px;
    margin: 0;
}

.billing-manage__compliance-badges {
    align-items: center;
    color: var(--billing-text);
    display: flex;
    flex-wrap: wrap;
    gap: 28px;
    justify-content: flex-end;
}

.billing-manage__compliance-badges span {
    align-items: center;
    display: inline-flex;
    font-size: 12px;
    font-weight: 800;
    gap: 8px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.billing-manage__admin-header,
.billing-manage__admin-toolbar {
    align-items: flex-start;
    display: flex;
    gap: 18px;
    justify-content: space-between;
}

.billing-manage__admin-header {
    margin-bottom: 28px;
}

.billing-manage__admin-header p {
    color: var(--billing-muted);
    font-size: 15px;
    line-height: 1.55;
    margin: 8px 0 0;
}

.billing-manage__admin-toolbar {
    align-items: center;
    margin-bottom: 18px;
}

.billing-manage__admin-toolbar .v-text-field {
    max-width: 420px;
}

.billing-manage__admin-toolbar .v-select {
    max-width: 200px;
}

.billing-manage__admin-table {
    overflow: hidden;
}

.billing-manage__admin-table-head,
.billing-manage__admin-row {
    align-items: center;
    display: grid;
    gap: 16px;
    grid-template-columns: 1.35fr 1.35fr 0.8fr 0.8fr 0.55fr;
    min-height: 60px;
    padding: 0 20px;
}

.billing-manage__admin-table-head {
    background: rgba(255, 255, 255, 0.035);
    color: var(--billing-text);
    font-size: 13px;
    font-weight: 800;
}

.billing-manage--light .billing-manage__admin-table-head {
    background: #edf1f8;
}

.billing-manage__admin-row {
    border-top: 1px solid var(--billing-border-soft);
    color: var(--billing-muted);
    font-size: 14px;
}

.billing-manage__admin-person {
    align-items: center;
    color: var(--billing-text);
    display: flex;
    gap: 10px;
    min-width: 0;
}

.billing-manage__admin-person strong,
.billing-manage__admin-email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.billing-manage__admin-avatar {
    align-items: center;
    background: linear-gradient(135deg, #6e4fc9, #3c64f4);
    border-radius: 999px;
    color: #ffffff;
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 800;
    height: 34px;
    justify-content: center;
    width: 34px;
}

.billing-manage__self-badge,
.billing-manage__role-badge {
    border-radius: 999px;
    display: inline-flex;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    padding: 5px 9px;
}

.billing-manage__self-badge {
    background: rgba(60, 100, 244, 0.22);
    color: #7da0ff;
    text-transform: uppercase;
}

.billing-manage__role-badge--owner {
    background: rgba(137, 99, 251, 0.18);
    color: #b8a1fd;
}

.billing-manage__role-badge--admin {
    background: rgba(60, 100, 244, 0.18);
    color: #7da0ff;
}

.billing-manage__admin-actions {
    text-align: right;
}

.billing-manage__admin-empty-row {
    border-top: 1px solid var(--billing-border-soft);
    color: var(--billing-muted);
    padding: 24px 20px;
    text-align: center;
}

.billing-manage__invite-card {
    align-items: center;
    border-style: dashed;
    display: flex;
    flex-direction: column;
    margin-top: 28px;
    min-height: 250px;
    justify-content: center;
    padding: 34px;
    text-align: center;
}

.billing-manage__invite-icon {
    align-items: center;
    background: rgba(60, 100, 244, 0.12);
    border: 1px solid rgba(60, 100, 244, 0.42);
    border-radius: 999px;
    color: #3c64f4;
    display: inline-flex;
    height: 70px;
    justify-content: center;
    margin-bottom: 18px;
    width: 70px;
}

.billing-manage__invite-card h3,
.billing-manage__roles-card h3 {
    color: var(--billing-text);
    font-size: 16px;
    font-weight: 800;
    margin: 0 0 10px;
}

.billing-manage__invite-card p {
    color: var(--billing-muted);
    line-height: 1.55;
    margin: 0 0 24px;
    max-width: 360px;
}

.billing-manage__roles-card {
    margin-top: 22px;
    padding: 22px;
}

.billing-manage__roles-grid {
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 22px;
}

.billing-manage__roles-grid > div {
    align-items: flex-start;
    display: flex;
    gap: 16px;
}

.billing-manage__roles-grid strong {
    color: var(--billing-text);
    display: block;
    font-size: 14px;
    margin-bottom: 6px;
}

.billing-manage__roles-grid p {
    color: var(--billing-muted);
    font-size: 13px;
    line-height: 1.45;
    margin: 0;
}

.billing-manage__admin-dialog {
    background: var(--billing-card-bg);
    border: 1px solid var(--billing-border);
    color: var(--billing-text);
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 22px;
}

.billing-manage__dialog-title,
.billing-manage__dialog-actions {
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
}

.billing-manage__dialog-title {
    margin-bottom: 20px;
}

.billing-manage__dialog-actions {
    margin-top: 20px;
}

.billing-manage__admin-search {
    width: 100%;
}

.billing-manage__admin-search :deep(.v-field__input) {
    min-height: 56px;
}

.billing-manage :deep(.v-field) {
    background: rgba(9, 24, 42, 0.18);
}

.billing-manage--light :deep(.v-field) {
    background: #ffffff;
}

@media (max-width: 1180px) {
    .billing-manage__metrics {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .billing-manage__body {
        grid-template-columns: 1fr;
    }

    .billing-manage__nav {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    .billing-manage__support {
        display: none;
    }
}

@media (max-width: 760px) {
    .billing-manage {
        padding: 22px 14px 40px;
    }

    .billing-manage__hero {
        flex-direction: column;
    }

    .billing-manage__actions {
        justify-content: flex-start;
        width: 100%;
    }

    .billing-manage__actions .v-btn {
        flex: 1 1 100%;
    }

    .billing-manage__metrics,
    .billing-manage__overview-grid,
    .billing-manage__contact-summary-grid,
    .billing-manage__plan-grid,
    .billing-manage__form-grid,
    .billing-manage__metadata-grid,
    .billing-manage__nav {
        grid-template-columns: 1fr;
    }

    .billing-manage__content {
        padding: 14px;
    }

    .billing-manage__contact-header,
    .billing-manage__admin-header,
    .billing-manage__admin-toolbar,
    .billing-manage__save-bar,
    .billing-manage__compliance-card {
        align-items: stretch;
        flex-direction: column;
    }

    .billing-manage__contact-actions,
    .billing-manage__admin-toolbar .v-text-field,
    .billing-manage__admin-toolbar .v-select,
    .billing-manage__save-bar .v-btn {
        max-width: none;
        width: 100%;
    }

    .billing-manage__admin-table {
        overflow-x: auto;
    }

    .billing-manage__admin-table-head,
    .billing-manage__admin-row {
        grid-template-columns: 210px 240px 140px 140px 90px;
        min-width: 820px;
    }

    .billing-manage__roles-grid {
        grid-template-columns: 1fr;
    }

    .billing-manage__subscription-title,
    .billing-manage__subscription-price {
        margin-left: 0;
    }

    .billing-manage__plan-facts div {
        grid-template-columns: 1fr;
    }
}
</style>
